/**
 * A person on the visit page — their initials in their own tone, as every
 * board of folder 50 draws them (Slice 1's shared helper, lead round 1 item 2).
 *
 * The tone is the person's `users.colour` through `toneOf()` — Activity's
 * faces, so a person reads the same on the calendar and on the visit. The one
 * face no user colour belongs to is the **customer** (People, Board 1): drawn
 * in the accent's soft pair, as the board draws Karen.
 */
import type { PersonRef } from '@rowted/shared'
import { Face as ActivityFace } from '../activity/faces.js'
import { cn } from '../lib/cn.js'

export function Face({ person, size = 22, customer = false, className }: {
  person: PersonRef
  /** 20 in Ready, 22 on a day tile, 26 on From the crew, 34 in People. */
  size?: number
  /** The customer's face — `accent-soft` / `accent-dark` (Board 1, People). */
  customer?: boolean
  className?: string
}) {
  if (customer) {
    return (
      <span
        title={person.name}
        className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-accent-soft font-bold tracking-[.02em] text-accent-dark', className)}
        style={{ width: size, height: size, fontSize: Math.round(Math.max(10, size * 0.34) * 10) / 10 }}
      >
        {person.initials}
      </span>
    )
  }
  return <ActivityFace person={person} size={size} {...(className ? { className } : {})} />
}

/**
 * The crew's faces overlapping — a 1.5 px white ring, each 6 px over the one
 * before (Board 1's day tiles, 22; Ready's crew row, 20).
 */
export function FaceRow({ people, size = 22, className }: { people: readonly PersonRef[]; size?: number; className?: string }) {
  return (
    <span className={cn('isolate inline-flex shrink-0 items-center', className)}>
      {people.map((person, index) => (
        <span
          key={person.id}
          className="relative inline-flex rounded-full border-[1.5px] border-surface"
          style={{ marginLeft: index === 0 ? 0 : -6, zIndex: people.length - index }}
        >
          <Face person={person} size={size} />
        </span>
      ))}
    </span>
  )
}
